// GA4 sink -- forwards enriched events to Google Analytics 4 via gtag.js.
//
// Division of labor: product decisions live in PostHog; GA4 is the
// acquisition/SEO surface and the fleet-dashboard feed (everbeach-analytics reads
// this same property, G-8QWT509DQD). Custom dimensions (source [USER], markup_type,
// format, measure_kind, doc_kind, tool [EVENT]) and Key Events (markup_created,
// export_completed, bimpdf_opened) are pre-registered on the property.
//
// ON only in production builds, and skipped per-event when the user opted out.

import type { EnrichedEvent } from '../events';
import { isRemoteOptedOut } from '../privacy';
import { isLocalBackendPage } from '@/services/backend';
import type { EventSink } from './types';
import { redactedLocation, redactedReferrer, onCapabilityRoute } from '../urlRedact';
import { inConsentRegion } from '../consentRegion';

const MEASUREMENT_ID =
    (import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined) || 'G-8QWT509DQD';

type Gtag = (...args: unknown[]) => void;

declare global {
    interface Window {
        dataLayer?: unknown[];
        gtag?: Gtag;
    }
}

let loaded = false;
let lastSource: string | null = null;

function ensureGtag(): Gtag | null {
    if (typeof window === 'undefined' || typeof document === 'undefined') return null;
    // Reuse an existing gtag if the page already loaded one (the static
    // index.html snippet does, and handles the initial page_view). Injecting a
    // second script + config for the same measurement id would double-count every
    // event and pageview. If a gtag is already present, just push events through it.
    if (typeof window.gtag === 'function') {
        loaded = true;
        return window.gtag;
    }
    if (!loaded) {
        loaded = true;
        const s = document.createElement('script');
        s.async = true;
        s.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
        document.head.appendChild(s);
        window.dataLayer = window.dataLayer || [];
        const gtag: Gtag = (...args: unknown[]) => {
            window.dataLayer!.push(args);
        };
        window.gtag = gtag;
        // Consent Mode, as index.html's snippet: analytics storage denied in the
        // consent region (no cookies), granted elsewhere; ads storage never.
        gtag('consent', 'default', {
            analytics_storage: inConsentRegion() ? 'denied' : 'granted',
            ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied',
        });
        // gtag() reads arguments-object identity, so pass a fixed epoch, not a value.
        gtag('js', new Date());
        gtag('set', { page_location: redactedLocation(), page_referrer: redactedReferrer() });
        gtag('config', MEASUREMENT_ID, { send_page_view: true, page_location: redactedLocation(), page_referrer: redactedReferrer() });
    }
    return window.gtag ?? null;
}

export const ga4Sink: EventSink = {
    id: 'ga4',
    // never on a LOCAL review session (services/backend.ts isLocalBackendPage), and
    // never on a capability route (/share/<token>, /join#<code>: the URL is the
    // credential and GA has no before_send -- urlRedact.ts isCapabilityPath)
    accepts: () => import.meta.env.PROD && !isRemoteOptedOut() && !isLocalBackendPage() && !onCapabilityRoute(),
    send(event: EnrichedEvent) {
        const gtag = ensureGtag();
        if (!gtag) return;
        // `source` is a set-once USER-scoped custom dimension (super-property shape).
        if (event.source !== lastSource) {
            lastSource = event.source;
            gtag('set', 'user_properties', { source: event.source });
        }
        // Do NOT pass `source` here -- it is a GA4 reserved event parameter that
        // overwrites session acquisition attribution. The user-scoped registration
        // above (gtag 'set' user_properties) is the correct mechanism. Use the
        // non-reserved name `actor_source` for per-event breakdowns instead.
        gtag('event', event.event, {
            actor_source: event.source,
            surface: event.surface,
            app_version: event.app_version,
            session_id: event.session_id,
            ...event.props,
        });
    },
};

/**
 * SPA navigation -> one GA4 page_view with the REDACTED location. The data
 * stream's "page changes based on browser history events" is OFF (2026-10):
 * GA would read the raw URL, and the local viewer's URL is /local/<file name>.
 * The `set` keeps every later event's page_location redacted too. The initial
 * page_view is index.html's `config`. No page_view at all for a capability
 * route (ga4Sink.accepts).
 */
export function gaPageChanged(): void {
    if (!ga4Sink.accepts({} as EnrichedEvent)) return;
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
    const page_location = redactedLocation();
    const page_referrer = redactedReferrer();
    window.gtag('set', { page_location, page_referrer });
    window.gtag('event', 'page_view', { page_location, page_referrer, page_title: document.title });
}
